import type { TooltipTriggerProps } from '@base-ui/react/tooltip';
import type { FloatingAlign, FloatingSide } from '../types/floating.ts';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import clsx from 'clsx';

export type TooltipProps = Pick<TooltipTriggerProps, 'closeDelay' | 'delay' | 'render'> &
  React.PropsWithChildren<{
    align?: FloatingAlign;
    content?: React.ReactNode;
    disabled?: boolean;
    maxWidth?: 'sm' | 'md' | 'lg';
    message?: React.ReactNode;
    side?: FloatingSide;
    size?: 'sm' | 'md';
  }>;

export function Tooltip({
  align = 'center',
  children,
  closeDelay = 0,
  content,
  delay = 150,
  disabled,
  maxWidth = 'md',
  message,
  render = <div />,
  side = 'bottom',
  size = 'md',
  ...triggerProps
}: TooltipProps) {
  return !disabled ? (
    <BaseTooltip.Root>
      <BaseTooltip.Trigger {...triggerProps} closeDelay={closeDelay} delay={delay} render={render}>
        {children}
      </BaseTooltip.Trigger>

      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          align={align}
          className="z-50 outline-none"
          positionMethod="fixed"
          side={side}
          sideOffset={8}
        >
          <BaseTooltip.Popup
            className={clsx(
              'transition-opacity duration-100 ease-out data-ending-style:opacity-0 data-starting-style:opacity-0 motion-reduce:transition-none',
              'relative z-50 rounded-md bg-gray-900 text-center text-white select-none',
              maxWidth === 'sm' && 'max-w-60',
              maxWidth === 'md' && 'max-w-sm',
              maxWidth === 'lg' && 'max-w-lg',
              size === 'sm' && 'px-2 py-1',
              size === 'md' && 'px-3 py-2'
            )}
          >
            <BaseTooltip.Arrow
              className={clsx(
                'size-2 rotate-45 bg-gray-900',
                'data-[side=bottom]:-top-1 data-[side=left]:-right-1 data-[side=right]:-left-1 data-[side=top]:-bottom-1'
              )}
            />

            {message ? <p className="text-xs leading-tight text-gray-100">{message}</p> : content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  ) : (
    children
  );
}
